/* eslint-disable css-modules/no-unused-class */
import classNames from 'classnames';
import React from 'react';
import Image from '@/components/atoms/image/image';
import Typography from '@/components/atoms/typography/typography';
import Video from '@/components/atoms/video/video';
import Splash from '@/components/molecules/splash/splash';
import CtaLinkList from '@/components/organisms/cta-link-list/cta-link-list';
import Column, {
  ColumnBreakPoints,
} from '@/components/utilities/column/column';
import Container from '@/components/utilities/container/container';
import Link from '@/components/utilities/link/link';
import Row from '@/components/utilities/row/row';
import { usePageDetailsContext } from '@/libraries/contexts/page-details-context/page-details-context';
import { formatImageUrl } from '@/libraries/formatters/format-image-url';
import { useWindowSize } from '@/libraries/hooks/use-window-size';
import primaryStyles from './header-carousel-item-primary.module.scss';
import secondaryStyles from './header-carousel-item-secondary.module.scss';

export interface HeaderCarouselItemProps {
  /** The type of item to show */
  $type:
    | 'HeaderCarouselOverlaySlideDto'
    | 'HeaderCarouselSlideDto'
    | 'HeaderCarouselMaskedSlideDto'
    | 'HeaderCarouselSimpleSlideDto';
  /** The class name to apply */
  className?: string;
  /** An array of links to show */
  ctaLinks?: Content.Components['HeaderCarouselSlideDto']['ctaLinks'];
  /** Current index of the carousel */
  currentIndex?: number;
  /** Text to show as description */
  description?: string;
  /** The splash in the first position */
  firstSplash?: Content.Components['HeaderCarouselSlideDto']['firstSplash'];
  /** Indicates whether the search bar is visible */
  hasSearchBar?: boolean;
  /** The text to show as a headline */
  headline?: string | null;
  /** Indicates whether autoplay is active */
  isPlaying?: boolean;
  /** Indicates whether the item is the primary item, used for SEO purposes */
  isPrimary?: boolean;
  /** The text to show as location */
  locationText?: string;
  /** The media to show */
  mediaItem?: Content.Components['HeaderCarouselSlideDto']['mediaItem'];
  /** Total number of the carousel items */
  pageCount?: number;
  /** The text to use as a caption */
  preHeadline?: Content.Components['HeaderCarouselSlideDto']['preHeadline'];
  /** The splash in the second position */
  secondSplash?: Content.Components['HeaderCarouselSlideDto']['secondSplash'];
  /** The settings for the component */
  settings?: Content.Components['HeaderCarouselSlideDto']['settings'];
  /** Variant of the header carousel item */
  variant?: string;
}

const generateColumnConfiguration = ({
  hasMask,
  isCentered,
  isRightAligned,
  variant,
}): { offset: ColumnBreakPoints; width: ColumnBreakPoints } => {
  if (isCentered) {
    return {
      offset: {
        desktop: 0,
        laptop: 0,
        tablet: 0,
      },
      width: { desktop: 12, laptop: 12, tablet: 12 },
    };
  }

  if (variant === 'secondary') {
    return {
      offset: {
        desktop: hasMask ? (isRightAligned ? 6 : 0) : 3,
        laptop: hasMask ? (isRightAligned ? 5 : 0) : 3,
        tablet: hasMask ? (isRightAligned ? 5 : 0) : 5,
      },
      width: { desktop: 5, tablet: 7 },
    };
  }

  return {
    offset: {
      desktop: isRightAligned ? 6 : hasMask ? 0 : 1,
      laptop: isRightAligned ? 5 : hasMask ? 0 : 1,
    },
    width: { desktop: 5, laptop: 6, tablet: 9 },
  };
};

const HeaderCarouselItem = ({
  $type,
  className,
  ctaLinks = [],
  description,
  firstSplash,
  hasSearchBar = false,
  headline,
  isPlaying = false,
  isPrimary = false,
  locationText,
  mediaItem,
  preHeadline,
  secondSplash,
  settings,
  variant,
}: HeaderCarouselItemProps) => {
  const isTablet = useWindowSize('tablet');
  const hasMask = $type === 'HeaderCarouselMaskedSlideDto';
  const hasOverlay = $type !== 'HeaderCarouselSimpleSlideDto';
  const isSimpleSlide = $type === 'HeaderCarouselSimpleSlideDto';
  const isLeftAligned = settings?.flippedLayout === true;
  const isRightAligned = settings?.flippedLayout !== true;
  const { hasTransparentNavigation } = usePageDetailsContext();
  const simpleSlideLinkUrl = isSimpleSlide && ctaLinks[0]?.link?.url;
  const simpleSlideLinkTarget = ctaLinks[0]?.link?.target ?? undefined;

  const backgroundImage = mediaItem
    ? 'image' in mediaItem
      ? mediaItem.image
      : 'video' in mediaItem
        ? mediaItem.video.placeholderImage
        : undefined
    : undefined;

  const styles = variant === 'secondary' ? secondaryStyles : primaryStyles;

  const renderWrapper = () => {
    const wrapperContent = (
      <div className={styles.wrapper}>
        <div className={styles.splashContainer}>
          {firstSplash && <Splash {...firstSplash} className={styles.splash} />}

          {secondSplash && isTablet && (
            <Splash {...secondSplash} className={styles.splash} />
          )}
        </div>

        {variant === 'primary' && locationText && (
          <Container className={styles.locationContainer}>
            <Typography className={styles.location} tagStyle="labelMedium">
              {locationText}
            </Typography>
          </Container>
        )}

        <Container className={styles.textContainer}>
          <Row className={styles.textRow}>
            <Column
              className={styles.textColumn}
              {...generateColumnConfiguration({
                hasMask,
                isCentered: isSimpleSlide,
                isRightAligned,
                variant,
              })}
            >
              <div className={styles.text}>
                {preHeadline && (
                  <div className={styles.preHeadline}>
                    <Typography tagStyle="labelMedium">
                      {preHeadline}
                    </Typography>
                  </div>
                )}

                {headline && (
                  <Typography
                    className={styles.headline}
                    tag={isPrimary ? 'h1' : 'h2'}
                    tagStyle="displayLarge"
                  >
                    {headline}
                  </Typography>
                )}

                {description && (
                  <Typography
                    className={styles.description}
                    tagStyle="bodyMedium"
                  >
                    {description}
                  </Typography>
                )}
              </div>

              {!isSimpleSlide && ctaLinks.length > 0 && (
                <div className={styles.links}>
                  <CtaLinkList items={ctaLinks} />
                </div>
              )}
            </Column>
          </Row>
        </Container>
      </div>
    );

    return isSimpleSlide && simpleSlideLinkUrl ? (
      <Link
        className={styles.wrapper}
        target={simpleSlideLinkTarget}
        url={simpleSlideLinkUrl}
      >
        {wrapperContent}
      </Link>
    ) : (
      wrapperContent
    );
  };

  return (
    <div
      className={classNames(styles.component, className, {
        [styles.hasMask]: hasMask,
        [styles.hasSearchBar]: hasSearchBar,
        [styles.hasTransparentNavigation]: hasTransparentNavigation,
        [styles.isLeftAligned]: isLeftAligned && !isSimpleSlide,
        [styles.isRightAligned]: isRightAligned && !isSimpleSlide,
        [styles.isCentered]: isSimpleSlide,
      })}
    >
      {mediaItem && 'video' in mediaItem ? (
        <React.Fragment>
          <Video
            autoPlay
            className={styles.video}
            isPlaying={isPlaying}
            placeholder={
              mediaItem?.video?.placeholderImage?.url
                ? formatImageUrl(mediaItem?.video?.placeholderImage?.url, {
                    width: 500,
                  })
                : undefined
            }
            preload={isPrimary ? 'auto' : 'none'}
            src={mediaItem.video.url}
          />

          {hasMask && (
            <div
              className={styles.mask}
              style={{ maskImage: `url(/images/headerCarousel-mask.svg)` }}
            >
              <Video
                autoPlay
                className={styles.innerVideo}
                isPlaying={isPlaying}
                placeholder={
                  mediaItem?.video?.placeholderImage?.url
                    ? formatImageUrl(mediaItem?.video?.placeholderImage?.url, {
                        width: 500,
                      })
                    : undefined
                }
                preload={isPrimary ? 'auto' : 'none'}
                src={mediaItem.video.url}
              />
            </div>
          )}
        </React.Fragment>
      ) : (
        backgroundImage && (
          <React.Fragment>
            <Image
              alt={backgroundImage.altText}
              className={styles.image}
              fill
              focalPoint={backgroundImage.focalPoint}
              height={552}
              priority={isPrimary}
              src={backgroundImage.url}
              srcSet={{
                desktop: {
                  height: 680,
                  width: 960,
                },
                laptop: {
                  height: 548,
                  width: 768,
                },
                mobile: {
                  height: 424,
                  width: 375,
                },
                tablet: {
                  height: 424,
                  width: 640,
                },
              }}
              unoptimized
              width={375}
            />

            {hasMask && backgroundImage && (
              <div
                className={styles.mask}
                style={{ maskImage: `url(/images/headerCarousel-mask.svg)` }}
              >
                <Image
                  alt={backgroundImage.altText}
                  className={styles.maskImage}
                  fill
                  focalPoint={backgroundImage.focalPoint}
                  height={552}
                  priority={isPrimary}
                  src={backgroundImage.url}
                  srcSet={{
                    desktop: {
                      height: 680,
                      width: 960,
                    },
                    laptop: {
                      height: 548,
                      width: 768,
                    },
                    mobile: {
                      height: 424,
                      width: 375,
                    },
                    tablet: {
                      height: 424,
                      width: 640,
                    },
                  }}
                  unoptimized
                  width={375}
                />
              </div>
            )}
          </React.Fragment>
        )
      )}

      {hasOverlay && <div className={styles.overlay} />}

      {renderWrapper()}
    </div>
  );
};

HeaderCarouselItem.displayName = 'HeaderCarouselItem';

export default HeaderCarouselItem;
